import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { getMemberDaybook } from '../../../../redux/actions/SocietyPortal/MemberDaybookAction/MemberDaybookAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { financialYears } from '../../../../consts/DropDownOptions';
import SearchableDropdown from '../../../../components/SearchableDropdown';

const getCurrentFY = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${year + 1}`;
};

const formatCurrency = (amount) => {
    const val = parseFloat(amount) || 0;
    if (val === 0) return '—';
    return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const MemberDaybook = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { memberDaybookData, loading } = useSelector(state => state.MemberDaybookReducer);

    const [financialYear, setFinancialYear] = useState(getCurrentFY());
    const [expandedAccounts, setExpandedAccounts] = useState({});

    // Fetch member daybook data
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (!societyId) return;
        dispatch(getMemberDaybook({ societyId, year: financialYear }));
    }, [dispatch, societyId, financialYear, refreshKey]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const accounts = memberDaybookData?.accounts || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const summary = memberDaybookData?.summary || {};

    const totals = useMemo(() => {
        let totalDaybooks = 0;
        accounts.forEach(acc => {
            totalDaybooks += (acc.daybooks || []).length;
        });
        return {
            totalAccounts: summary.total_accounts ?? accounts.length,
            totalDaybooks: summary.total_daybooks ?? totalDaybooks,
            grandTotalDebit: summary.grand_total_dr ?? 0,
            grandTotalCredit: summary.grand_total_cr ?? 0,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts, summary]);

    const toggleAccount = (accountId) => {
        setExpandedAccounts(prev => ({
            ...prev,
            [accountId]: !prev[accountId],
        }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-5">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-gradient-to-br tw-from-violet-500 tw-to-purple-600 tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-shadow-violet-500/20">
                        <Icon icon="mdi:book-account" className="tw-text-xl tw-text-white" />
                    </div>
                    <div>
                        <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Member Daybook (Opening)</h1>
                        <p className="tw-text-[11px] tw-text-gray-400">View member-wise daybook opening balances</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <SearchableDropdown
                        value={financialYear}
                        onChange={setFinancialYear}
                        options={financialYears}
                        placeholder="Financial Year"
                        clearable={false}
                        small
                    />
                </div>
            </div>

            {/* Summary Card */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 tw-mb-4 tw-shadow-sm">
                <div className="tw-flex tw-items-center tw-gap-8">
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Total Accounts</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-0.5 mb-0">{totals.totalAccounts}</p>
                    </div>
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Total Daybooks</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-0.5 mb-0">{totals.totalDaybooks}</p>
                    </div>
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Grand Total Debit</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-0.5 mb-0">
                            <span className="tw-text-sm tw-font-normal tw-text-gray-400 tw-mr-0.5">&#8377;</span>
                            {parseFloat(totals.grandTotalDebit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Grand Total Credit</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-0.5 mb-0">
                            <span className="tw-text-sm tw-font-normal tw-text-gray-400 tw-mr-0.5">&#8377;</span>
                            {parseFloat(totals.grandTotalCredit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
                <table className="tw-w-full">
                    <thead>
                        <tr className="tw-border-b tw-border-gray-100 tw-bg-gray-50/50">
                            <th className="tw-text-left tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[40%]">Account / Daybook</th>
                            <th className="tw-text-left tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[20%]">Account Code</th>
                            <th className="tw-text-right tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[20%]">Debit (&#8377;)</th>
                            <th className="tw-text-right tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[20%]">Credit (&#8377;)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="4" className="tw-text-center tw-py-20">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                                        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-animate-pulse">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-blue-200 tw-border-t-blue-600"></div>
                                        </div>
                                        <span className="tw-text-gray-400 tw-text-xs">Loading daybook data...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : accounts.length > 0 ? (
                            accounts.map(acc => {
                                const isExpanded = expandedAccounts[acc.account_id];
                                const daybooks = acc.daybooks || [];

                                return (
                                    <AccountRow
                                        key={acc.account_id}
                                        account={acc}
                                        daybooks={daybooks}
                                        isExpanded={isExpanded}
                                        onToggle={() => toggleAccount(acc.account_id)}
                                    />
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="4" className="tw-text-center tw-py-20">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                        <div className="tw-w-14 tw-h-14 tw-rounded-2xl tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
                                            <Icon icon="mdi:book-account" className="tw-text-3xl tw-text-gray-300" />
                                        </div>
                                        <p className="tw-text-gray-400 tw-text-xs tw-font-medium">No daybook data found</p>
                                        <p className="tw-text-gray-300 tw-text-[10px]">No member accounts with daybook entries for this year</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const AccountRow = ({ account, daybooks, isExpanded, onToggle }) => {
    const hasDaybooks = daybooks.length > 0;

    return (
        <>
            {/* Parent Account Row */}
            <tr
                className={`tw-border-b tw-border-gray-50 tw-transition-colors ${hasDaybooks ? 'tw-cursor-pointer hover:tw-bg-gray-50/50' : ''}`}
                onClick={hasDaybooks ? onToggle : undefined}
            >
                <td className="tw-px-5 tw-py-3">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        {hasDaybooks && (
                            <Icon
                                icon={isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                                className="tw-text-gray-400 tw-text-base tw-flex-shrink-0"
                            />
                        )}
                        <span className="tw-text-xs tw-font-semibold tw-text-gray-800">{account.account_name}</span>
                    </div>
                </td>
                <td className="tw-px-5 tw-py-3">
                    <code className="tw-text-[11px] tw-text-gray-500 tw-bg-gray-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium">{account.account_cd}</code>
                </td>
                <td className="tw-px-5 tw-py-3 tw-text-right">
                    <span className={`tw-text-xs tw-font-medium ${(parseFloat(account.total_dr_amount) || 0) > 0 ? 'tw-text-gray-800' : 'tw-text-gray-300'}`}>
                        {formatCurrency(account.total_dr_amount)}
                    </span>
                </td>
                <td className="tw-px-5 tw-py-3 tw-text-right">
                    <span className={`tw-text-xs tw-font-medium ${(parseFloat(account.total_cr_amount) || 0) > 0 ? 'tw-text-gray-800' : 'tw-text-gray-300'}`}>
                        {formatCurrency(account.total_cr_amount)}
                    </span>
                </td>
            </tr>

            {/* Daybook Child Rows */}
            {isExpanded && daybooks.map((db, idx) => (
                <tr key={`${account.account_id}-db-${db.daybook_id || idx}`} className="tw-border-b tw-border-gray-50 tw-bg-gray-50/30">
                    <td className="tw-px-5 tw-py-2.5 tw-pl-12" colSpan="2">
                        <span className="tw-text-xs tw-text-amber-700 tw-font-medium">{db.daybook_name}</span>
                    </td>
                    <td className="tw-px-5 tw-py-2.5 tw-text-right">
                        <span className={`tw-text-xs tw-font-medium ${(parseFloat(db.dr_amount) || 0) > 0 ? 'tw-text-gray-700' : 'tw-text-gray-300'}`}>
                            {formatCurrency(db.dr_amount)}
                        </span>
                    </td>
                    <td className="tw-px-5 tw-py-2.5 tw-text-right">
                        <span className={`tw-text-xs tw-font-medium ${(parseFloat(db.cr_amount) || 0) > 0 ? 'tw-text-gray-700' : 'tw-text-gray-300'}`}>
                            {formatCurrency(db.cr_amount)}
                        </span>
                    </td>
                </tr>
            ))}
        </>
    );
};

export default MemberDaybook;
